import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Self
} from '@angular/core';
import { FormControlName, NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { InputRootController } from '@shared/components/input/root/input-root-controller.directive';
import { InputType } from '@shared/components/input/root/input-type';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { TranslateService } from '@ngx-translate/core';

/**
 * A directive to provide enhanced functionality for select elements, extending the InputRootController.
 * The directive sets up additional behaviors for select inputs by leveraging Angular's form control mechanisms
 * and change detection strategies.
 *
 * This directive uses dependency injection to retrieve and control relevant Angular forms API components,
 * such as NgModel and FormControlName, if available. These components are utilized to manage and synchronize
 * the state of the bound select element with the parent form or model.
 *
 * Key Features:
 * - Provides compatibility with Angular forms (Template-driven forms and Reactive forms).
 * - Automatically integrates with ChangeDetectorRef to manage view updates efficiently.
 * - Extends the capabilities of basic input elements by adding additional control and interaction layers.
 * - Supports automatic sorting by translated values.
 * - Supports numeric value conversion for form controls.
 * - Synchronizes form control values with select element display.
 *
 * Selector: `[appSelect]`
 *
 * Providers:
 * - Replaces `InputType` with `SelectDirective` for dependency injection scope.
 */
@Directive({
  selector: '[appSelect]',
  providers: [{ provide: InputType, useExisting: SelectDirective }],
})
export class SelectDirective extends InputRootController implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() public collection?: KeyValue[];
  @Input() public nullOption?: boolean = true;
  @Input() public numericValues?: boolean = false;
  @Input() public sortByTranslation?: boolean = false;

  /** Allow autofocus via boolean binding or bare attribute — mirrors InputDirective behaviour */
  private _autofocus = false;
  @Input()
  set autofocus(v: boolean | string | null | undefined) {
    this._autofocus = v === '' || v === true || v === 'true';
  }

  private observer?: MutationObserver;

  public collectionCarrier: KeyValue[] = [];

  private valueSub?: Subscription;
  private changeHandlerRef?: (e: Event) => void;

  constructor(
    @Optional() @Self() @Inject(NgModel) protected override ngModel: NgModel,
    @Optional() @Self() @Inject(FormControlName) protected override formControl: FormControlName,
    protected override cdr: ChangeDetectorRef,
    protected override elementRef: ElementRef<HTMLSelectElement>,
    private readonly translateService: TranslateService
  ) {
    super(ngModel, formControl, cdr, elementRef);
  }

  public ngOnInit(): void {
    const control = this.ngModel?.control ?? this.formControl?.control;
    if (control) {
      this.valueSub = control.valueChanges.subscribe((val) => {
        this.updateNativeSelect(val);
      });
      // initial sync
      this.updateNativeSelect(control.value);
    }

  }

  public ngOnChanges(): void {
    // Pushing execution to the next tick to ensure the collection is fully loaded
    setTimeout(() => {
      this.buildOptions();
    });
  }

  public ngAfterViewInit(): void {
    if (!this._autofocus) return;
    // Use the same MutationObserver + rAF strategy as InputDirective so that
    // focus fires reliably in Angular SPA navigation (native autofocus attr doesn't).
    this.observer = new MutationObserver(() => {
      if (this.isVisible()) {
        this.focusIfNeeded();
        this.observer?.disconnect();
      }
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    if (this.isVisible()) {
      this.focusIfNeeded();
      this.observer?.disconnect();
    }
  }

  public ngOnDestroy(): void {
    if (this.valueSub) {
      this.valueSub.unsubscribe();
      this.valueSub = undefined;
    }
    if (this.changeHandlerRef) {
      this.elementRef.nativeElement.removeEventListener('change', this.changeHandlerRef);
      this.changeHandlerRef = undefined;
    }
    this.observer?.disconnect();
  }

  private isVisible(): boolean {
    return this.elementRef.nativeElement.offsetParent !== null;
  }

  private focusIfNeeded(): void {
    if (!this._autofocus) return;
    const el = this.elementRef.nativeElement;
    if (el.disabled) return;
    requestAnimationFrame(() => {
      if (this.isVisible()) el.focus({ preventScroll: true });
    });
  }

  private buildOptions(): void {
    if (this.collection) {
      if (this.collection === this.collectionCarrier && !this.sortByTranslation) return;

      this.clearOptions();

      // Create a working copy of the collection
      let workingCollection = [...this.collection];

      // Sort by translation if requested
      if (this.sortByTranslation) {
        workingCollection = this.sortCollectionByTranslation(workingCollection);
      }

      this.collectionCarrier = workingCollection;

      if (this.nullOption) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = `${this.translateService.instant('common.select')} ${this.internalLabel ?? ''}`;
        this.elementRef.nativeElement.appendChild(option);
      }

      for (const element of workingCollection) {
        const option = document.createElement('option');
        option.value = element.key;
        option.textContent = this.translateService.instant(element.value);
        this.elementRef.nativeElement.appendChild(option);
      }

      // Synchronize with form control value after options are built
      this.synchronizeValue();

      // Add change listener to convert values if needed
      this.setupValueTransformation();
    }
  }

  private synchronizeValue(): void {
    const formControlValue = this.getFormControlValue();
    this.updateNativeSelect(formControlValue);
  }

  private getFormControlValue(): any {
    if (this.ngModel?.control) {
      return this.ngModel.control.value;
    } else if (this.formControl?.control) {
      return this.formControl.control.value;
    }
    return null;
  }

  private sortCollectionByTranslation(collection: KeyValue[]): KeyValue[] {
    return collection.sort((a, b) => {
      const translatedA = this.translateService.instant(a.value);
      const translatedB = this.translateService.instant(b.value);
      return translatedA.localeCompare(translatedB);
    });
  }

  private updateNativeSelect(value: any): void {
    const el = this.elementRef.nativeElement;
    const normalized = value === null || value === undefined || value === '' ? '' : String(value);

    // If the desired value doesn't exist among options and it's not the empty placeholder,
    // do nothing (let the browser keep current); for empty we explicitly clear selection.
    if (normalized === '') {
      el.value = '';
      // If there is no empty option, clear selection index to show nothing selected
      if (el.value !== '') {
        el.selectedIndex = -1;
      }
    } else {
      el.value = normalized;
    }

    this.cdr.detectChanges();
  }

  private setupValueTransformation(): void {
    const element = this.elementRef.nativeElement;

    // Remove existing listener to prevent duplicates
    if (this.changeHandlerRef) {
      element.removeEventListener('change', this.changeHandlerRef);
    }

    this.changeHandlerRef = (event: Event) => this.handleValueChange(event);
    element.addEventListener('change', this.changeHandlerRef);
  }

  private handleValueChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const stringValue = target.value;

    if (stringValue === '') {
      // Handle null option consistently for all modes
      this.updateFormControl(null);
    } else if (this.numericValues) {
      const numericValue = Number(stringValue);
      if (Number.isNaN(numericValue)) {
        this.updateFormControl(stringValue);
      } else {
        this.updateFormControl(numericValue);
      }
    } else {
      this.updateFormControl(stringValue);
    }
  }

  private updateFormControl(value: any): void {
    if (this.ngModel) {
      // emitEvent: false suppresses valueChanges, which prevents the valueSub
      // from calling updateNativeSelect in a feedback loop. emitModelToViewChange
      // stays true (the default), so the model-to-view pipeline still runs:
      // viewToModelUpdate fires with the converted value, updating the bound
      // property and emitting ngModelChange once with the correct type.
      this.ngModel.control.setValue(value, { emitEvent: false });
    } else if (this.formControl) {
      this.formControl.control.setValue(value);
    }
  }

  private clearOptions(): void {
    if (this.elementRef.nativeElement.hasChildNodes()) {
      this.elementRef.nativeElement.innerHTML = '';
    }
  }
}
